'use client';
import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from 'react-router-dom';
import MainCard from "../../components/cards/MainCard";
import {
    Box,
    Grid,
    Card,
    CardContent,
    CardActions,
    Typography,
    Button,
    Alert,
    Chip,
    IconButton,
    Tooltip
} from "@mui/material";
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SchoolIcon from '@mui/icons-material/School';
import TimerIcon from '@mui/icons-material/Timer';
import InfoIcon from '@mui/icons-material/Info';

export default function ScormCourseList() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const email = searchParams.get('emailAddress');
    const [courses, setCourses] = useState([]);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!email) {
            navigate('/');
            return;
        }

        // Load SCORM courses from environment configuration
        const scormCoursesConfig = process.env.REACT_APP_SCORM_COURSES;
        if (!scormCoursesConfig) {
            setError('SCORM courses not configured. Please add REACT_APP_SCORM_COURSES to your environment configuration.');
            return;
        }

        try {
            const parsedCourses = JSON.parse(scormCoursesConfig);
            setCourses(parsedCourses);
        } catch (err) {
            setError('Error parsing SCORM courses configuration');
            console.error('Error parsing SCORM courses:', err);
        }
    }, [email, navigate]);

    const handleStartCourse = (courseId, uniqueIdentifier) => {
        navigate(`/takescormcourse/${courseId}?emailAddress=${encodeURIComponent(email)}`);
    };

    if (!email) {
        return null;
    }

    if (error) {
        return (
            <MainCard content={false} sx={{ width: "100%", bgcolor: "common.white", p: 3 }} border={false} boxShadow={true}>
                <Alert severity="error">
                    <Typography variant="h6">Configuration Error</Typography>
                    <Typography>{error}</Typography>
                </Alert>
            </MainCard>
        );
    }

    if (courses.length === 0) {
        return (
            <MainCard content={false} sx={{ width: "100%", bgcolor: "common.white", p: 3 }} border={false} boxShadow={true}>
                <Box textAlign="center" py={5}>
                    <SchoolIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="h5" color="text.secondary" gutterBottom>
                        No SCORM Courses Available
                    </Typography>
                    <Typography variant="body1" color="text.disabled">
                        There are currently no SCORM courses configured for this environment.
                    </Typography>
                </Box>
            </MainCard>
        );
    }

    return (
        <MainCard content={false} sx={{ width: "100%", bgcolor: "common.white", p: 3 }} border={false} boxShadow={true}>
            <Box mb={3}>
                <Typography variant="h4" gutterBottom>
                    Available SCORM Courses
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Select a course below to begin your training
                </Typography>
            </Box>

            <Grid container spacing={3}>
                {courses.map((course) => (
                    <Grid item xs={12} sm={6} md={4} key={course.id}>
                        <Card
                            sx={{
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                transition: 'transform 0.2s, box-shadow 0.2s',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: 4
                                }
                            }}
                        >
                            <CardContent sx={{ flexGrow: 1 }}>
                                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                                    <SchoolIcon color="primary" sx={{ fontSize: 32 }} />
                                    {course.category && (
                                        <Chip
                                            label={course.category}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                        />
                                    )}
                                </Box>

                                <Typography variant="h6" gutterBottom>
                                    {course.title}
                                </Typography>

                                {course.description && (
                                    <Typography variant="body2" color="text.secondary" paragraph>
                                        {course.description}
                                    </Typography>
                                )}

                                <Box display="flex" gap={2} mt={2}>
                                    {course.duration && (
                                        <Box display="flex" alignItems="center" gap={0.5}>
                                            <TimerIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                            <Typography variant="caption" color="text.secondary">
                                                {course.duration}
                                            </Typography>
                                        </Box>
                                    )}

                                    {course.difficulty && (
                                        <Chip
                                            label={course.difficulty}
                                            size="small"
                                            variant="outlined"
                                            sx={{ height: 20 }}
                                        />
                                    )}
                                </Box>

                                {course.topics && course.topics.length > 0 && (
                                    <Box mt={2}>
                                        <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                                            Topics covered:
                                        </Typography>
                                        <Box display="flex" gap={0.5} flexWrap="wrap">
                                            {course.topics.map((topic, index) => (
                                                <Chip
                                                    key={index}
                                                    label={topic}
                                                    size="small"
                                                    sx={{ fontSize: '0.75rem' }}
                                                />
                                            ))}
                                        </Box>
                                    </Box>
                                )}
                            </CardContent>

                            <CardActions sx={{ p: 2, pt: 0 }}>
                                {JSON.stringify(course)}
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<PlayArrowIcon />}
                                    fullWidth
                                    onClick={() => handleStartCourse(course.id)}
                                >
                                    Start Course
                                </Button>

                                {course.info && (
                                    <Tooltip title={course.info}>
                                        <IconButton size="small">
                                            <InfoIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </CardActions>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </MainCard>
    );
}